/* eslint-disable max-len */
import { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import NPSE_Layout from '../../../../components/FormLayouts/NPSE/NPSE_Layout';
import ManualInputPage from '../../../../components/ManualInputPage/ManualInputPage';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  clearNonPublicStudent,
  saveNonPublicStudent,
  selectNonPublicStudentDataEntryMode,
  selectNonPublicStudentFormViewModel,
  selectNonPublicStudentLoadingFormViewModel,
  selectNonPublicStudentLoadingNonPublicStudent,
  selectNonPublicStudentNonPublicStudent,
  selectSchoolYear,
  setNonPublicStudentDataEntryMode,
} from '../../../../redux/slices/nonPublicStudentSlice';
import {
  fetchNonPublicStudentById,
  fetchNonPublicStudentFormViewModel,
  fetchNonPublicStudentScheduleByNpsId,
  fetchNpsSchoolYears,
} from '../../../../redux/thunks/nonPublicStudentThunks';
import {
  abortPromiseOnUnmount,
  getCurrentUserAccess,
} from '../../../../services/base.service';
import {
  createNonPublicStudent,
  updateNonPublicStudent,
} from '../../../../services/nonPublicStudent.service';
import { appRoutePaths } from '../../../../services/route.service';
import { GLOBAL_DEBUG_MODE } from '../../../../testing/debugFunctions';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { Tabs_STUDENT } from '../../../../types/NavigationTabs';
import { NonPublicStudentEntryModel } from '../../../../types/NonPublicStudentEntryModel';
import { NonPublicStudentFormViewModel } from '../../../../types/NonPublicStudentFormViewModel';
import { ManualInputCrudAction } from '../../../../types/propTypes/ManualInputPagePropTypes';
import { NonPublicStudentEntryValidationSchema } from '../../../../types/Validation/NonPublicStudentEntryValidationSchema';
import { buildUserAccessNavigationTabItems } from '../../../../utilities/routeUtilities';
import { formatDistrictForDisplay } from '../../../../utilities/textUtilities';

const formatSSNForDisplay = (value: string, previousValue = ''): string => {
  if (!value) return '';
  const cleanValue = value.replace(/[^\d-]/g, '');

  const digitsOnly = cleanValue.replace(/\D/g, '');
  const truncatedDigits = digitsOnly.substring(0, 9);
  const userTypedDash =
    value.length > previousValue.length && value.endsWith('-');
  if (truncatedDigits.length === 0) return '';
  if (truncatedDigits.length <= 3) {
    return userTypedDash && truncatedDigits.length === 3
      ? `${truncatedDigits}-`
      : truncatedDigits;
  } else if (truncatedDigits.length <= 5) {
    const part1 = truncatedDigits.substring(0, 3);
    const part2 = truncatedDigits.substring(3);
    const formatted = `${part1}-${part2}`;

    return userTypedDash && truncatedDigits.length === 5
      ? `${formatted}-`
      : formatted;
  } else {
    const part1 = truncatedDigits.substring(0, 3);
    const part2 = truncatedDigits.substring(3, 5);
    const part3 = truncatedDigits.substring(5);

    return `${part1}-${part2}-${part3}`;
  }
};

const NonPublicStudentEntryInput = (): ReactElement => {
  const { schoolYear: urlSchoolYear } = useParams<{ schoolYear: string }>();
  const dispatch = useAppDispatch();

  const model: NonPublicStudentEntryModel = useAppSelector(
    selectNonPublicStudentNonPublicStudent
  );
  const formViewModel: NonPublicStudentFormViewModel = useAppSelector(
    selectNonPublicStudentFormViewModel
  );
  const isLoadingFormViewModel = useAppSelector(
    selectNonPublicStudentLoadingFormViewModel
  );
  const isLoadingModel = useAppSelector(
    selectNonPublicStudentLoadingNonPublicStudent
  );
  const dataEntryMode: DataEntryMode = useAppSelector(
    selectNonPublicStudentDataEntryMode
  );
  const schoolYear = useAppSelector(selectSchoolYear);
  const isLoading = isLoadingModel || isLoadingFormViewModel;

  const getDistrictDisplay = (): string => {
    if (dataEntryMode === DataEntryMode.EDIT) {
      return model.district;
    } else {
      const user = getCurrentUserAccess();
      return formatDistrictForDisplay(user?.currentDistrict);
    }
  };

  useEffect(() => {
    let promise: unknown;
    if (model.id.length > 0 && dataEntryMode === DataEntryMode.EDIT) {
      promise = dispatch(fetchNonPublicStudentScheduleByNpsId(model.id));
    }

    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch, dataEntryMode, model]);

  const getSchoolYear = (): string => {
    if (dataEntryMode == DataEntryMode.EDIT) {
      return model.schoolYear;
    } else {
      // Priority: URL parameter > Redux state > form model default
      if (urlSchoolYear && urlSchoolYear !== ':schoolYear') {
        return urlSchoolYear;
      } else if (schoolYear !== '') {
        return schoolYear;
      } else {
        return formViewModel.defaultSchoolYear;
      }
    }
  };

  // TODO may need to make this an ACUTAL property that comes
  // from the form model or make sure this process will work going forward.
  /* istanbul ignore next */
  const getYearForAgeEvaluation = (): string => {
    const schoolYearDisplay = getSchoolYear();
    const years = schoolYearDisplay.split('-');

    if (years.length > 1) {
      return years[0].trim();
    }

    const yearNumber = parseInt(years[0].trim(), 10);
    if (!isNaN(yearNumber)) {
      return (yearNumber - 1).toString();
    }

    return years[0].trim();
  };

  const getSchoolDisplay = (): string => {
    return dataEntryMode === DataEntryMode.EDIT
      ? `${model.schoolName} (${model.schoolId})`
      : '';
  };

  const intitalValues: NonPublicStudentEntryModel = {
    ...model,
    district: getDistrictDisplay(),
    schoolYear: getSchoolYear().split('-')[1] || getSchoolYear(),
    ageEvaluationYear: getYearForAgeEvaluation(),
    hasMatch: dataEntryMode === DataEntryMode.EDIT,
    entryType: 'Initial Enrollment for the School Year',
    school: getSchoolDisplay(),
    personIdentifierSsn: formatSSNForDisplay(model.personIdentifierSsn || ''),
  };

  // Fetch school year options on mount (similar to NonPublicStudentEntryList)
  useEffect(() => {
    const promise: unknown = dispatch(fetchNpsSchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  const createSuccessRoute = (newId?: string): string => {
    if (newId) {
      return appRoutePaths.NonPublicStudentEntryInput(newId, schoolYear);
    } else {
      return appRoutePaths.NonPublicStudentEntryInput('new', schoolYear);
    }
  };

  return (
    <ManualInputPage<NonPublicStudentEntryModel, NonPublicStudentFormViewModel>
      actions={ManualInputCrudAction.CREATE_AND_EDIT}
      loadingOptions={{
        isLoading: isLoading,
        loadingDataId: 'non-public-student-entry-loader',
      }}
      navigationTabs={buildUserAccessNavigationTabItems(Tabs_STUDENT)}
      classes={{
        pageClass: 'data-input--student--non-public-student-entry--form',
      }}
      parameterOptions={{
        routeParameterName: 'nonPublicStudentId',
        identityName: model.id,
      }}
      routes={{
        backBarRoute: appRoutePaths.NonPublicStudentEntryList,
        cancelRoute: appRoutePaths.NonPublicStudentEntryList,
        createSuccessRoute: createSuccessRoute,
      }}
      dataFunctions={{
        loadDataDetailById: fetchNonPublicStudentById,
        loadFormDetail: fetchNonPublicStudentFormViewModel,
        setEntryMode: setNonPublicStudentDataEntryMode,
        setData: saveNonPublicStudent,
        clearData: clearNonPublicStudent,
        onCreate: createNonPublicStudent,
        onUpdate: updateNonPublicStudent,
      }}
      formDetails={{
        validationSchema: NonPublicStudentEntryValidationSchema,
        initialFormValues: intitalValues,
        isFormModelEmpty: formViewModel.schoolTypeOptions.length === 0,
      }}
      config={{
        dataEntryMode: dataEntryMode,
        objectVerbiage: 'non public student',
        pageHeader: 'Student',
      }}
      DEV_MODE={GLOBAL_DEBUG_MODE}
      REDIRECT_TO_LIST_PAGE={false}
    >
      <NPSE_Layout />
    </ManualInputPage>
  );
};

export default NonPublicStudentEntryInput;
